import { AnimatePresence } from "framer-motion";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ReactComponent as Arrow } from "../../../assets/svg/arrow-up-right.svg";
import { ReactComponent as Close } from "../../../assets/svg/close.svg";
import { LINKS } from "../../../constants/links";
import { PageEndpointEnum } from "../../../enums/endpoint";
import { useIntercomHook } from "../../../hooks/intercom";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import AboutMarginXModal from "../Header/AboutMarginXModal";
import { Container, NavItem, NavItems, SubNavItem } from "./style";
import { COMPONENTS, SPACING } from "../../../constants/design/spacing";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { BUILD_VERSION, BUILD_TIME, COMMIT_HASH } from "../../../buildInfo";

interface IMobileNavigationMenuProps {
  open: boolean;
  onClose: () => void;
}

function MobileNavigationMenu({ open, onClose }: IMobileNavigationMenuProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "shared.MobileNavigationMenu",
  });
  const [showAboutMarginXModal, setShowAboutMarginXModal] = useState(false);
  const { handleIntercomButtonClicked } = useIntercomHook();

  const onIntercomButtonClicked = useCallback(() => {
    onClose();
    handleIntercomButtonClicked();
  }, [handleIntercomButtonClicked, onClose]);

  const openURL = useCallback(
    (url: string) => {
      onClose();
      window.open(url);
    },
    [onClose]
  );

  return (
    <AnimatePresence>
      <AboutMarginXModal
        show={showAboutMarginXModal}
        onHide={() => setShowAboutMarginXModal(false)}
      />

      {open && (
        <Container
          key={"container"}
          style={{ position: "fixed" }}
          transition={{
            duration: 0.4,
            ease: "easeInOut",
          }}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
        >
          <div>
            <Button
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={onClose}
              style={{
                minWidth: COMPONENTS.header.buttons,
                minHeight: COMPONENTS.header.buttons,
                padding: 0,
              }}
            >
              <Close />
            </Button>
          </div>
          <NavItems>
            {/*
              Home is intentionally hidden from the mobile More menu.
              To restore it, re-enable this block.

              <NavItem>
                <button type="button" onClick={() => openURL(LINKS.home)}>
                  {t("home")} <Arrow />
                </button>
              </NavItem>
            */}
            <NavItem>
              <Link to={PageEndpointEnum.WATCHLIST} onClick={onClose}>
                {t("watchlist")}
              </Link>
            </NavItem>
            <NavItem>
              <Link to={PageEndpointEnum.LEADERBOARD} onClick={onClose}>
                {t("leaderboard")}
              </Link>
            </NavItem>
            <NavItem>
              <button type="button" onClick={() => openURL(LINKS.docs)}>
                {t("docs")} <Arrow />
              </button>
            </NavItem>
            <NavItem>
              <button type="button" onClick={() => openURL(LINKS.apidocs)}>
                {t("api_reference")} <Arrow />
              </button>
            </NavItem>
            <NavItem>
              <button type="button" onClick={onIntercomButtonClicked}>
                {t("help_and_support")} <Arrow />
              </button>
            </NavItem>
            {/*
              Historical Data is intentionally hidden from the mobile More menu.
              To restore it, re-enable the Link/PageEndpointEnum imports and this block.

              <NavItem>
                <Link to={PageEndpointEnum.HISTORICAL} onClick={onClose}>
                  {t("historical_data")}
                </Link>
              </NavItem>
            */}
          </NavItems>

          <NavItems>
            <SubNavItem onClick={() => openURL(LINKS.twitter)}>
              {t("twitter")} <Arrow />
            </SubNavItem>
            <SubNavItem onClick={() => openURL(LINKS.discord)}>
              {t("discord")} <Arrow />
            </SubNavItem>
            <SubNavItem onClick={() => openURL(LINKS.github)}>
              {t("github")} <Arrow />
            </SubNavItem>
            <SubNavItem onClick={() => openURL(LINKS.blog)}>
              {t("blog")} <Arrow />
            </SubNavItem>
            <SubNavItem onClick={() => openURL(LINKS.mediakit)}>
              {t("media_kit")} <Arrow />
            </SubNavItem>
          </NavItems>

          <div
            title={`commit ${COMMIT_HASH}\nbuilt ${BUILD_TIME}`}
            style={{
              color: TEXT_COLORS.three,
              fontSize: FONT_SIZE.xs,
              fontFamily: "'JetBrains Mono', monospace",
              paddingLeft: SPACING.two,
              userSelect: "text",
            }}
          >
            v{BUILD_VERSION}
          </div>
        </Container>
      )}
    </AnimatePresence>
  );
}
export default MobileNavigationMenu;
